import React from 'react';
import { InteractionState, SolveMode } from '../../types';
import { getAvailableScenarios, type ScenarioType } from '../../scenarios/networks';
import { MenuBar, type TopMenu } from '../common/Menu';
import './Toolbar.css';

interface ToolbarProps {
  interactionState: InteractionState;
  setInteractionState: (state: InteractionState) => void;
  onExport: () => void;
  onImport: () => void;
  onNewScenario: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  currentScenario: ScenarioType;
  documentName: string;
  onScenarioChange: (scenario: ScenarioType) => void;
  mode: SolveMode;
  onModeChange: (mode: SolveMode) => void;
}

// ⌘ on Mac, Ctrl elsewhere — just for display in the menu.
const modKey =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl+';

export function Toolbar({
  interactionState,
  setInteractionState,
  onExport,
  onImport,
  onNewScenario,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  currentScenario,
  documentName,
  onScenarioChange,
  mode,
  onModeChange,
}: ToolbarProps) {
  const scenarios = getAvailableScenarios();

  const menus: TopMenu[] = [
    {
      label: 'Solve',
      items: [
        {
          kind: 'action',
          label: 'DC optimal power flow',
          checked: mode === 'dcopf',
          onClick: () => onModeChange('dcopf'),
        },
        {
          kind: 'action',
          label: 'AC power flow',
          checked: mode === 'acpf',
          onClick: () => onModeChange('acpf'),
        },
      ],
    },
    {
      label: 'Scenario',
      items: [
        { kind: 'action', label: 'New', icon: '📄', onClick: onNewScenario },
        { kind: 'separator' },
        {
          kind: 'submenu',
          label: 'Load',
          items: scenarios.map((scenario) => ({
            kind: 'action' as const,
            label: scenario.name,
            checked: scenario.id === currentScenario,
            onClick: () => onScenarioChange(scenario.id),
          })),
        },
      ],
    },
    {
      label: 'Edit',
      items: [
        {
          kind: 'action',
          label: 'Undo',
          icon: '↩️',
          shortcut: `${modKey}Z`,
          disabled: !canUndo,
          onClick: onUndo,
        },
        {
          kind: 'action',
          label: 'Redo',
          icon: '↪️',
          shortcut: `${modKey}⇧Z`,
          disabled: !canRedo,
          onClick: onRedo,
        },
      ],
    },
    {
      label: 'File',
      items: [
        { kind: 'action', label: 'Import…', onClick: onImport },
        { kind: 'action', label: 'Export…', onClick: onExport },
      ],
    },
    {
      label: 'Add',
      items: [
        {
          kind: 'action',
          label: 'Generator',
          icon: '⚡',
          onClick: () => setInteractionState({ mode: 'adding-node', nodeType: 'generator' }),
        },
        {
          kind: 'action',
          label: 'Load',
          icon: '💡',
          onClick: () => setInteractionState({ mode: 'adding-node', nodeType: 'load' }),
        },
        {
          kind: 'action',
          label: 'Bus',
          icon: '🚌',
          onClick: () => setInteractionState({ mode: 'adding-node', nodeType: 'bus' }),
        },
        {
          kind: 'action',
          label: 'Battery',
          icon: '🔋',
          onClick: () => setInteractionState({ mode: 'adding-node', nodeType: 'battery' }),
        },
        {
          kind: 'action',
          label: 'Line',
          icon: '➖',
          onClick: () => setInteractionState({ mode: 'adding-edge-select-first' }),
        },
      ],
    },
  ];

  return (
    <div className="menu-container">
      <MenuBar menus={menus} />

      {interactionState.mode !== 'normal' && (
        <button
          onClick={() => setInteractionState({ mode: 'normal' })}
          className="menu-button menu-button-active menu-button-cancel"
        >
          Cancel
        </button>
      )}

      {/* Current document title (the loaded scenario name) */}
      <div className="menu-document-title" title="Current scenario">
        {documentName || 'Untitled'}
      </div>
    </div>
  );
}
